import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Dialog, TextField, Box, Typography, Alert } from "@mui/material";
import { Client } from "../../@data";
import { hasValidSurname } from "../../utils";
import { updateClient } from "../../services/clients";
import Button from "../button";
import ModalCloseButton from "../modal-close-button";
import {
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
} from "../../styles/modal-styles";

export interface SurnameModalRef {
  open: (client: Client, onSuccess?: () => void) => void;
  close: () => void;
}

interface SurnameModalProps {
  loading?: boolean;
}

const SurnameModal = forwardRef<SurnameModalRef, SurnameModalProps>(
  ({ loading = false }, ref) => {
    const [open, setOpen] = useState(false);
    const [client, setClient] = useState<Client | null>(null);
    const [fullName, setFullName] = useState("");
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [onSuccessCallback, setOnSuccessCallback] = useState<
      (() => void) | null
    >(null);

    useImperativeHandle(ref, () => ({
      open: (clientData: Client, onSuccess?: () => void) => {
        setClient(clientData);
        setFullName(clientData.name || "");
        setErrors([]);
        setOnSuccessCallback(() => onSuccess || null);
        setOpen(true);
      },
      close: () => {
        setOpen(false);
        setClient(null);
        setFullName("");
        setErrors([]);
        setOnSuccessCallback(null);
      },
    }));

    const handleClose = () => {
      if (!loading) {
        setOpen(false);
        setClient(null);
        setFullName("");
        setErrors([]);
        setOnSuccessCallback(null);
      }
    };

    const handleConfirm = async () => {
      const newErrors: string[] = [];

      if (!fullName.trim()) {
        newErrors.push("Nome completo é obrigatório");
      } else if (!hasValidSurname(fullName)) {
        newErrors.push(
          "Por favor, informe seu nome completo com sobrenome (mínimo 3 caracteres)"
        );
      }

      if (newErrors.length > 0) {
        setErrors(newErrors);
        return;
      }

      if (!client || !client.id) {
        setErrors(["Erro: Cliente não identificado"]);
        return;
      }

      setSaving(true);
      setErrors([]);

      try {
        // Atualiza o nome no banco de dados
        await updateClient(client.id, { name: fullName.trim() });

        // Executa o callback (que deve atualizar o cliente no contexto)
        if (onSuccessCallback) {
          onSuccessCallback();
        }

        handleClose();
      } catch (error) {
        console.error("Erro ao salvar sobrenome:", error);
        setErrors(["Erro ao salvar. Tente novamente."]);
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1.5rem" } }}
        disableEscapeKeyDown={loading || saving}
      >
        <StyledDialogTitle sx={{ pb: 1 }}>
          Complete seu cadastro
          <ModalCloseButton
            onClose={handleClose}
            disabled={loading || saving}
          />
        </StyledDialogTitle>

        <StyledDialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Para facilitar a organização do salão, precisamos que você informe
              seu nome completo com sobrenome.
            </Typography>

            {errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Nome e sobrenome"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              margin="normal"
              required
              helperText="Informe seu nome completo com sobrenome (mínimo 3 caracteres no sobrenome)"
            />
          </Box>
        </StyledDialogContent>

        <StyledDialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={
              loading ||
              saving ||
              !fullName.trim() ||
              !hasValidSurname(fullName)
            }
            loading={saving}
            loadingText="Salvando..."
          >
            Salvar
          </Button>
        </StyledDialogActions>
      </Dialog>
    );
  }
);

SurnameModal.displayName = "SurnameModal";

export default SurnameModal;
